import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, AlertTriangle, CheckCircle, Clock, Sparkles, Loader2, Zap, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  urgent: 'bg-red-100 text-red-800 border-red-300',
  high:   'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  low:    'bg-blue-100 text-blue-800 border-blue-300',
};
const STATUS_STYLES = {
  open:        'bg-red-100 text-red-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved:    'bg-green-100 text-green-800',
  closed:      'bg-gray-100 text-gray-700',
};
const CATEGORY_ICONS = {
  plumbing: '🚰', electrical: '💡', cleaning: '🧹', security: '🔒',
  internet: '📶', appliance: '📺', furniture: '🪑', noise: '🔊', other: '📋',
};

const Complaints = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, open: 0, urgent: 0, by_status: {}, by_priority: {} });
  const [meta, setMeta] = useState({ categories: [], priorities: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [routing, setRouting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, sRes, mRes] = await Promise.all([
        axios.get(`${API}/complaints`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/complaints/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/complaints/meta`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(lRes.data);
      setSummary(sRes.data);
      setMeta(mRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load complaints', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (c, status) => {
    try {
      await axios.put(`${API}/complaints/${c.id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `Marked as ${status}` });
      fetchData();
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleAIRoute = async (c) => {
    setRouting(c.id);
    try {
      const res = await axios.post(`${API}/complaints/${c.id}/ai-route`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({
        title: `✨ AI Routed: ${res.data.priority.toUpperCase()} • ${res.data.category}`,
        description: `${res.data.reasoning} (SLA: ${res.data.sla_hours}h)`
      });
      fetchData();
    } catch (e) {
      toast({ title: 'AI routing failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setRouting(null); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/complaints/${deleting.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Complaint deleted' });
      setDeleting(null); fetchData();
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const filtered = items.filter(i => {
    const s = search.toLowerCase();
    const matchesSearch = !s || i.title?.toLowerCase().includes(s) || i.description?.toLowerCase().includes(s) || i.tenant_name?.toLowerCase().includes(s);
    const matchesStatus = !statusFilter || i.status === statusFilter;
    const matchesPriority = !priorityFilter || i.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="complaints-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><AlertTriangle /> Complaints</h1>
          <p className="text-gray-600 mt-1">AI-routed tickets with priority & SLA</p>
        </div>
        <Button data-testid="add-complaint-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus size={18} className="mr-2" /> New Complaint
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 border-l-4 border-l-gray-500"><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold mt-1">{summary.total}</p></Card>
        <Card className="p-4 border-l-4 border-l-red-500"><p className="text-xs text-gray-500">Open</p><p className="text-2xl font-bold text-red-700 mt-1">{summary.open}</p></Card>
        <Card className="p-4 border-l-4 border-l-yellow-500"><p className="text-xs text-gray-500">In Progress</p><p className="text-2xl font-bold text-yellow-700 mt-1">{summary.by_status?.in_progress || 0}</p></Card>
        <Card className="p-4 border-l-4 border-l-green-500"><p className="text-xs text-gray-500">Resolved</p><p className="text-2xl font-bold text-green-700 mt-1">{summary.by_status?.resolved || 0}</p></Card>
        <Card className="p-4 border-l-4 border-l-red-700 bg-red-50"><p className="text-xs text-red-700">Urgent 🚨</p><p className="text-2xl font-bold text-red-800 mt-1">{summary.urgent}</p></Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="complaints-search"
            type="text"
            placeholder="Search title, description, tenant..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
          />
        </div>
        <select data-testid="complaint-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
          <option value="">All Statuses</option>
          {meta.statuses?.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="complaint-priority-filter" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
          <option value="">All Priorities</option>
          {meta.priorities?.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="complaints-empty">
          <CheckCircle size={64} className="mx-auto text-green-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2">All clear — no complaints</h3>
          <p className="text-gray-600 mb-4">Create a complaint or report a tenant issue</p>
          <Button onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700"><Plus size={18} className="mr-2" /> New Complaint</Button>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="complaints-list">
          {filtered.map(c => (
            <Card key={c.id} className={`p-5 border-l-4 ${c.priority === 'urgent' ? 'border-l-red-600 bg-red-50/30' : c.priority === 'high' ? 'border-l-orange-500' : 'border-l-gray-300'}`} data-testid={`complaint-row-${c.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-2xl">{CATEGORY_ICONS[c.category] || '📋'}</span>
                    <h3 className="font-semibold text-gray-900 text-lg">{c.title}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium border ${PRIORITY_STYLES[c.priority]}`}>{c.priority}</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[c.status]}`}>{c.status.replace('_', ' ')}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{c.category}</span>
                    {c.ai_sla_hours && <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1"><Clock size={10}/> SLA {c.ai_sla_hours}h</span>}
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{c.description}</p>
                  <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                    <span>📍 {c.property_name || 'Property'}{c.room_number && ` • Room ${c.room_number}`}</span>
                    {c.tenant_name && <span>👤 {c.tenant_name}</span>}
                    <span>🕐 {new Date(c.created_at).toLocaleString()}</span>
                    {c.ai_suggested_role && <span className="text-purple-600">✨ Assign to: {c.ai_suggested_role}</span>}
                  </div>
                  {c.ai_reasoning && (
                    <p className="text-xs text-purple-700 mt-2 italic">AI: {c.ai_reasoning}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-1">
                    <button
                      data-testid={`complaint-ai-route-${c.id}`}
                      onClick={() => handleAIRoute(c)}
                      disabled={routing === c.id}
                      className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded hover:opacity-90 disabled:opacity-50"
                      title="AI Route"
                    >
                      {routing === c.id ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                    </button>
                    <button data-testid={`complaint-edit-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 text-gray-600 hover:bg-gray-100 rounded"><Edit2 size={14} /></button>
                    <button data-testid={`complaint-delete-${c.id}`} onClick={() => setDeleting(c)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                  </div>
                  <select
                    data-testid={`complaint-status-change-${c.id}`}
                    value={c.status}
                    onChange={e => handleStatusChange(c, e.target.value)}
                    className="text-xs px-2 py-1 border rounded"
                  >
                    {meta.statuses?.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <ComplaintFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchData}
          editing={editing}
          meta={meta}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Complaint"
        message="Are you sure?"
        itemName={deleting?.title || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

const ComplaintFormModal = ({ open, onClose, onSuccess, editing, meta, token }) => {
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', property_id: '', tenant_id: '', room_id: '',
    category: 'other', priority: 'medium', reported_by: '',
  });
  const [useAI, setUseAI] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [pRes, tRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tenants`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProperties(pRes.data); setTenants(tRes.data);
    })();
    if (editing) {
      setForm({
        title: editing.title || '', description: editing.description || '',
        property_id: editing.property_id || '', tenant_id: editing.tenant_id || '',
        room_id: editing.room_id || '',
        category: editing.category || 'other', priority: editing.priority || 'medium',
        reported_by: editing.reported_by || '',
      });
      setUseAI(false);
    }
  }, [token, editing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tenant_id: form.tenant_id || null,
        room_id: form.room_id || null,
      };
      if (editing) {
        await axios.put(`${API}/complaints/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Complaint updated' });
      } else if (useAI) {
        const res = await axios.post(`${API}/complaints/ai-create`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({
          title: `✨ AI Routed: ${res.data.priority?.toUpperCase()} • ${res.data.category}`,
          description: res.data.ai_reasoning || ''
        });
      } else {
        await axios.post(`${API}/complaints`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Complaint created' });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit Complaint' : 'New Complaint'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="complaint-form">
          {!editing && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="text-purple-600" size={18} />
                <span className="text-sm font-medium text-purple-900">AI Auto-Routing</span>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="complaint-use-ai" type="checkbox" checked={useAI} onChange={e => setUseAI(e.target.checked)} />
                <span className="font-medium">{useAI ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
          )}
          <div><Label>Title *</Label><Input data-testid="complaint-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Short issue summary" required /></div>
          <div>
            <Label>Description *</Label>
            <textarea data-testid="complaint-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} required className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Describe the issue in detail..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Property *</Label>
              <select data-testid="complaint-property" value={form.property_id} onChange={e => setForm({...form, property_id: e.target.value, tenant_id: '', room_id: ''})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                <option value="">Select</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Tenant (Optional)</Label>
              <select data-testid="complaint-tenant" value={form.tenant_id} onChange={e => setForm({...form, tenant_id: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">None</option>
                {tenants.filter(t => !form.property_id || t.property_id === form.property_id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            {(!useAI || editing) && (
              <>
                <div>
                  <Label>Category</Label>
                  <select data-testid="complaint-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {meta.categories?.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c] || '📋'} {c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <select data-testid="complaint-priority" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {meta.priorities?.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="complaint-submit" type="submit" className="flex-1 bg-red-600 hover:bg-red-700" disabled={saving}>
              {saving ? <><Loader2 className="animate-spin mr-2" size={14} /> Saving...</> : (editing ? 'Update' : (useAI ? '✨ Create with AI' : 'Create'))}
            </Button>
            <Button data-testid="complaint-cancel" type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Complaints;
